import { useCallback, useState } from 'react'

import { useFormikContext } from 'formik'

import { useAppDispatch } from '@user/store/hooks'
import { validateAssignmentRuleCoverage } from '@user/store/leadAssignmentRule'

import { parseCoverageError } from '../../../utils/coverageError'
import type { ConditionModel, LeadAssignmentRuleFormModel } from '../../typings'

export function useHandlers() {
  const [editValues, setEditValues] = useState<ConditionModel | undefined>()
  const [open, setOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [coverageBlockLabels, setCoverageBlockLabels] = useState<
    string[] | null
  >(null)
  const dispatch = useAppDispatch()
  const {
    setFieldValue,
    values: { id, assignmentRuleConditions },
  } = useFormikContext<LeadAssignmentRuleFormModel>()

  /** Resolves to false when the server blocks the edit, so the modal stays open. */
  const saveCondition = useCallback(
    async (condition: ConditionModel) => {
      const updatedConditions = [...assignmentRuleConditions]

      if (selectedIndex !== -1) {
        updatedConditions.splice(selectedIndex, 1, {
          ...condition,
          assignmentRuleConditionValues: [
            ...condition.assignmentRuleConditionValues,
          ],
        })
      } else {
        updatedConditions.push(condition)
      }

      if (id) {
        const result = await dispatch(
          validateAssignmentRuleCoverage({
            id,
            assignmentRuleConditions: updatedConditions,
          }),
        )
        const labels = parseCoverageError(result.payload)
        if (labels) {
          setCoverageBlockLabels(labels)
          return false
        }
      }

      setEditValues(() => undefined)
      setFieldValue('assignmentRuleConditions', updatedConditions)
      return true
    },
    [
      assignmentRuleConditions,
      dispatch,
      id,
      setEditValues,
      setFieldValue,
      selectedIndex,
    ],
  )

  const closeCoverageBlock = useCallback(() => setCoverageBlockLabels(null), [])

  const removeCondition = useCallback(
    (idx: number) => {
      const updatedConditions = [...assignmentRuleConditions]
      updatedConditions.splice(idx, 1)

      setFieldValue('assignmentRuleConditions', updatedConditions)
    },
    [assignmentRuleConditions, setFieldValue],
  )

  const editCondition = useCallback(
    (value: ConditionModel, index: number) => {
      setSelectedIndex(index)
      setEditValues(() => value)
      setOpen(true)
    },
    [setEditValues],
  )

  const cancelCondition = useCallback(() => {
    setEditValues(() => undefined)
    setOpen(false)
  }, [])

  const addCondition = useCallback(() => {
    setSelectedIndex(-1)
    setEditValues(() => undefined)
    setOpen(true)
  }, [])

  return {
    saveCondition,
    removeCondition,
    editCondition,
    setEditValues,
    editValues,
    open,
    setOpen,
    cancelCondition,
    addCondition,
    coverageBlockLabels,
    closeCoverageBlock,
  }
}
